/**
 * CostEstimateDialog Component
 * Shows token cost estimate BEFORE analysis - no surprises for user
 *
 * Features:
 * - Clear cost display
 * - Balance after calculation
 * - Low balance warning with purchase CTA
 * - Insufficient balance handling (opens PricingModal)
 * - User consent checkbox (REQUIRED before analysis)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SecurityIcon from '@mui/icons-material/Security';
import { getFileTypeConfig } from '../../../api/qbridgeEaApi';

/**
 * CostEstimateDialog Component
 * @param {boolean} open - Dialog visibility
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm analysis handler (receives consentGiven boolean)
 * @param {function} onBuyTokens - Open pricing modal handler
 * @param {object} file - Selected file object
 * @param {number} estimatedCost - Estimated token cost
 * @param {number} currentBalance - User's current token balance
 * @param {boolean} hasSufficientBalance - Whether user has enough tokens (from API)
 * @param {string} purchaseUrl - URL to purchase more tokens (from 402 response)
 * @param {boolean} isLoading - Loading state while fetching estimate
 * @param {string} error - Error message if any
 */
const CostEstimateDialog = ({
  open = false,
  onClose,
  onConfirm,
  onBuyTokens,
  file = null,
  estimatedCost = 5,
  currentBalance = 0,
  hasSufficientBalance = true,
  purchaseUrl = null,
  isLoading = false,
  error = null
}) => {
  // Local consent state - resets when dialog opens
  const [consentChecked, setConsentChecked] = useState(false);

  // Reset consent when dialog opens/closes
  useEffect(() => {
    if (open) {
      setConsentChecked(false);
    }
  }, [open]);

  if (!file) return null;

  const balanceAfter = currentBalance - estimatedCost;
  const hasInsufficientBalance = !hasSufficientBalance || balanceAfter < 0;
  const isLowBalance = !hasInsufficientBalance && balanceAfter >= 0 && balanceAfter <= 20;
  const fileConfig = getFileTypeConfig(file.file_type);

  // Handle confirm with consent
  const handleConfirm = () => {
    if (consentChecked && onConfirm) {
      onConfirm(true); // Pass consent=true to parent
    }
  };

  // Handle buy tokens - open in new tab if purchaseUrl provided
  const handleBuyTokens = () => {
    if (purchaseUrl) {
      window.open(purchaseUrl, '_blank');
    } else if (onBuyTokens) {
      onBuyTokens();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0F0F14',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          maxWidth: '420px'
        }
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#FFFFFF', fontSize: '1.25rem' }} />
          </Box>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '1.125rem',
              fontWeight: 600,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            Analyze with Q.AI
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ════════════════════════════════════════════════════════════════════════
          CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogContent sx={{ padding: '24px' }}>
        {/* File Info */}
        <Box
          sx={{
            padding: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* File Type Badge */}
            <Box
              sx={{
                backgroundColor: `${fileConfig.color}20`,
                color: fileConfig.color,
                fontWeight: 700,
                fontSize: '0.6875rem',
                padding: '4px 8px',
                borderRadius: '4px',
                border: `1px solid ${fileConfig.color}40`
              }}
            >
              {fileConfig.label}
            </Box>
            {/* File Name */}
            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '0.9375rem',
                fontWeight: 600,
                fontFamily: "'JetBrains Mono', monospace",
                wordBreak: 'break-all'
              }}
            >
              {file.name}
            </Typography>
          </Box>
          {/* File Stats */}
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8125rem',
              mt: 1
            }}
          >
            {file.line_count?.toLocaleString() || '?'} lines
            {(typeof file.size_kb === 'number') && ` • ${file.size_kb.toFixed(1)} KB`}
          </Typography>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 3,
              gap: 2
            }}
          >
            <CircularProgress size={32} sx={{ color: '#7c3aed' }} />
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>
              Calculating cost estimate...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              '& .MuiAlert-icon': { color: '#EF4444' }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Cost Breakdown */}
        {!isLoading && !error && (
          <>
            {/* Estimated Cost */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                borderRadius: '12px',
                border: '1px solid rgba(124, 58, 237, 0.2)',
                mb: 2
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}
              >
                Estimated cost
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BoltIcon sx={{ color: '#a855f7', fontSize: '1.125rem' }} />
                <Typography
                  sx={{
                    color: '#a855f7',
                    fontSize: '1.125rem',
                    fontWeight: 700
                  }}
                >
                  ~{estimatedCost} tokens
                </Typography>
              </Box>
            </Box>

            {/* Balance After */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                backgroundColor: hasInsufficientBalance
                  ? 'rgba(239, 68, 68, 0.08)'
                  : isLowBalance
                    ? 'rgba(251, 191, 36, 0.08)'
                    : 'rgba(39, 181, 109, 0.08)',
                borderRadius: '12px',
                border: `1px solid ${
                  hasInsufficientBalance
                    ? 'rgba(239, 68, 68, 0.2)'
                    : isLowBalance
                      ? 'rgba(251, 191, 36, 0.2)'
                      : 'rgba(39, 181, 109, 0.2)'
                }`
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9375rem',
                  fontWeight: 500
                }}
              >
                {hasInsufficientBalance ? 'Tokens needed' : 'Balance after'}
              </Typography>
              <Typography
                sx={{
                  color: hasInsufficientBalance
                    ? '#EF4444'
                    : isLowBalance
                      ? '#FBBF24'
                      : '#27B56D',
                  fontSize: '1.125rem',
                  fontWeight: 700
                }}
              >
                {hasInsufficientBalance
                  ? `${Math.abs(balanceAfter)} more`
                  : `${balanceAfter} tokens`}
              </Typography>
            </Box>

            {/* Insufficient Balance Warning */}
            {hasInsufficientBalance && (
              <Box
                sx={{
                  mt: 2,
                  padding: '16px',
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  borderRadius: '12px',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <WarningAmberIcon sx={{ color: '#EF4444', fontSize: '1.25rem', mt: 0.25 }} />
                <Box>
                  <Typography
                    sx={{
                      color: '#EF4444',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    Insufficient token balance
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '0.8125rem'
                    }}
                  >
                    You have {currentBalance} tokens but need {estimatedCost}. Purchase more tokens to analyze this EA.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Low Balance Warning */}
            {!hasInsufficientBalance && isLowBalance && (
              <Box
                sx={{
                  mt: 2,
                  padding: '12px 16px',
                  backgroundColor: 'rgba(251, 191, 36, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(251, 191, 36, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <WarningAmberIcon sx={{ color: '#FBBF24', fontSize: '1rem' }} />
                <Typography
                  sx={{
                    color: '#FBBF24',
                    fontSize: '0.8125rem'
                  }}
                >
                  Low balance after this analysis. Consider adding more tokens.
                </Typography>
              </Box>
            )}

            {/* ─────────────────────────────────────────────────────────────────────────
                CONSENT CHECKBOX - Required before analysis
                ───────────────────────────────────────────────────────────────────────── */}
            {!hasInsufficientBalance && (
              <Box
                sx={{
                  mt: 3,
                  padding: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        '&.Mui-checked': {
                          color: '#27B56D'
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.25rem'
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <SecurityIcon sx={{ color: '#27B56D', fontSize: '1rem', mt: 0.25 }} />
                      <Typography
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.8125rem',
                          lineHeight: 1.5
                        }}
                      >
                        I consent to Q.AI analyzing my code. My original files will never be modified.
                        Modified versions are saved with <code style={{
                          backgroundColor: 'rgba(124, 58, 237, 0.2)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          color: '#a855f7'
                        }}>_QAI</code> suffix.
                      </Typography>
                    </Box>
                  }
                  sx={{
                    alignItems: 'flex-start',
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      marginLeft: 0.5
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* ════════════════════════════════════════════════════════════════════════
          FOOTER
          ════════════════════════════════════════════════════════════════════════ */}
      <DialogActions
        sx={{
          padding: '16px 24px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          gap: 1.5
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textTransform: 'none',
            fontWeight: 500,
            padding: '10px 20px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Cancel
        </Button>

        {hasInsufficientBalance ? (
          <Button
            onClick={handleBuyTokens}
            sx={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              padding: '10px 24px',
              borderRadius: '8px',
              '&:hover': {
                background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)'
              }
            }}
          >
            Get More Tokens
          </Button>
        ) : (
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !!error || !consentChecked}
            sx={{
              background: consentChecked
                ? 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)'
                : 'rgba(39, 181, 109, 0.3)',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              padding: '10px 24px',
              borderRadius: '8px',
              '&:hover': {
                background: consentChecked
                  ? 'linear-gradient(135deg, #22A55E 0%, #16A34A 100%)'
                  : 'rgba(39, 181, 109, 0.3)'
              },
              '&.Mui-disabled': {
                background: 'rgba(39, 181, 109, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {consentChecked ? 'Confirm Analysis' : 'Check consent to continue'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CostEstimateDialog;
